import {
    grispiSubscriptionModal,
    subscriptionAttachmentLimitMap,
    subscriptionFeatureMap,
    subscriptionLimitMap
} from '../containers/subscription/SubscriptionController';
import { grispiNotification } from '../store/useNotificationStore';

interface RequestWithMessageProps {
    key: string;
    startMessage?: string;
    finishMessage?: string;
    apiRequest: () => Promise<any>;
    errorConfig?: {
        onApiRequestError?: (error: Error) => void;
        showAutoErrorMessage?: boolean;
        isPublicApiRequest?: boolean;
    };
}

const defaultErrorConfig = { onApiRequestError: null, showAutoErrorMessage: true, isPublicApiRequest: false };
/**
 * It takes a function that returns a promise, and it shows a loading message while the promise is
 * pending, and it shows a success message when the promise resolves, and it shows an error message
 * when the promise rejects
 * @param {string} key - string - The key of the message.
 * @param {string} startMessage - The message to show when the request starts.
 * @param {string} finishMessage - The message to show when the request is successful.
 * @param apiRequest - () => Promise<any>
 * @param [onApiRequestError] - This is a callback function that will be called if the API request
 * fails.
 * @param [showAutoErrorMessage=true] - If true, the error message will be shown automatically. If
 * false, the error message will not be shown automatically so that you should handle error message to be shown in @onApiRequestError callback function, and the error will be passed to the
 * onApiRequestError callback.
 * @param [isPublicApiRequest=false] - if request is public subscription errors will ignored.
 */
const requestWithMessage = async ({
    key,
    startMessage,
    finishMessage,
    apiRequest,
    errorConfig = {}
}: RequestWithMessageProps): Promise<void> => {
    const { onApiRequestError, showAutoErrorMessage, isPublicApiRequest } = { ...defaultErrorConfig, ...errorConfig };
    try {
        startMessage && grispiNotification('loading', startMessage, key, 0);

        await apiRequest();

        finishMessage && grispiNotification('success', finishMessage, key);
        !finishMessage && grispiNotification('destroy', '', key);
    } catch (error) {
        if (!isPublicApiRequest) {
            const limitKey = error?.response?.data?.limitKey;
            if (limitKey) {
                const limit = Object.keys(subscriptionLimitMap).find(
                    (limit) => typeof limitKey === 'string' && limitKey.includes(limit)
                );
                if (limit) {
                    grispiNotification('destroy', '', key);
                    grispiSubscriptionModal({ limit: subscriptionLimitMap[limit] });
                    if (onApiRequestError) {
                        onApiRequestError(new Error('limit exceeded'));
                    }
                    return;
                }
                const feature = Object.keys(subscriptionFeatureMap).find(
                    (feature) => typeof limitKey === 'string' && limitKey.includes(feature)
                );
                if (feature) {
                    grispiNotification('destroy', '', key);
                    grispiSubscriptionModal({ feature: subscriptionFeatureMap[feature] });
                    if (onApiRequestError) {
                        onApiRequestError(new Error('unsupported feature'));
                    }
                    return;
                }
                const attachment = Object.keys(subscriptionAttachmentLimitMap).find(
                    (attachment) => typeof limitKey === 'string' && limitKey.includes(attachment)
                );
                if (attachment) {
                    grispiNotification('destroy', '', key);
                    grispiSubscriptionModal({ limit: subscriptionAttachmentLimitMap[attachment] });
                    if (onApiRequestError) {
                        onApiRequestError(new Error(attachment));
                    }
                    return;
                }
            }
        }
        if (!onApiRequestError) {
            grispiNotification('error', error?.response?.data?.message ?? error?.message, key);
            throw error;
        } else {
            if (showAutoErrorMessage) {
                grispiNotification('error', error?.response?.data?.message ?? error?.message, key);
            } else grispiNotification('destroy', '', key);
            onApiRequestError(error);
        }
    }
};

export default requestWithMessage;
